/**
 * Engine surface for the generic relationship tree, configured by the field's
 * `parameters/customFieldTypes` entry with form context from `formStore`. Mark
 * and unmark run their bridging workflows first and commit only on success, so
 * a trophy never outlives a failed entity reference (SYD-2569) — one-way: a run
 * stopped after `setEntityReference` landed leaves the reference on an unmarked
 * tree. `interactionDisabled` disables every mutating control while the shared
 * runner is busy, and a post-await `commit` reads `treesRef`, not the array
 * captured at click time. `runWorkflowFromTree` returns an outcome because the
 * parameter sidebar latches its Run button on it (SYD-2442); expansion is
 * stored inverted as `collapsedKeys` so async-hydrated nodes default to open.
 * Below `md` the tree is capped at `--form-content-max-width` and scrolls
 * sideways inside itself, so its row minimum widths never widen the form page.
 */
import { FC, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Button, Grid, theme } from 'antd';
import { MailOutlined, PlusOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useSnapshot } from 'valtio';

import type {
  RelationshipTreeConfig,
  RelationshipTreeEntityConfig,
  RelationshipTreeNode,
  RelationshipTreeValue,
} from '#types/System.RelationshipTree.types';
import type { Workflow, WorkflowRunAttachments } from '#types';

import FlexBox from '@/components/atoms/FlexBox';
import { FORM_PAGE_CSS_VARS } from '@/components/atoms/FORM_PAGE_CSS_VARS';
import { formStore } from '@/store';
import { MessageContext } from '@/providers/MessageContext';
import { useApplicableWorkflows } from '@/use/useApplicableWorkflows';
import { useWorkflowRunner } from '@/workflows/runtime/useWorkflowRunner';
import type { WorkflowRunOutcome } from '@/workflows/runtime/workflowRunOutcome.types';
import { workflowRunErrorOutcome } from '@/workflows/runtime/workflowRunErrorOutcome';
import { workflowRunStoppedOutcome } from '@/workflows/runtime/workflowRunStoppedOutcome';
import WorkflowParameterSidebar from '@/workflows/runtime/WorkflowParameterSidebar';

import {
  collectRecordIds,
  computeWinningKeySet,
  computeWinningRecordIds,
  countDescendants,
  createNode,
  duplicateNode,
  findMarkWinningWorkflows,
  findNode,
  findSendWorkflowsForNode,
  findUnmarkWinningWorkflows,
  getNodeBranch,
  getNodeDepth,
  hasCompletePathWithRecords,
  insertChild,
  insertSibling,
  MAX_TREE_DEPTH,
  removeNode,
  updateNode,
  validateCombinationUniqueness,
} from '#types';
import { buildNodeMenu } from './menu';
import NodeRow from './nodeRow';
import TreeNode from './treeNode';

type RelationshipTreeProps = {
  value: RelationshipTreeValue | null | undefined;
  onChange: (value: RelationshipTreeValue) => void;
  config: RelationshipTreeConfig | undefined;
  disabled?: boolean;
  status?: '' | 'error' | 'warning' | undefined;
  id?: string;
};

type PendingDelete = { nodeKey: string; message: string };

const buildEntityHierarchy = (
  rootEntityId: string,
  config: RelationshipTreeConfig,
): string[] => {
  const hierarchy: string[] = [];
  const visited = new Set<string>();
  let current: string | undefined = rootEntityId;
  while (current && !visited.has(current)) {
    hierarchy.push(current);
    visited.add(current);
    const entry: RelationshipTreeEntityConfig | undefined =
      config.entityConfig[current];
    current = entry?.allowedChildren?.[0];
  }
  return hierarchy;
};

const collectAllKeys = (trees: RelationshipTreeNode[]): Set<string> => {
  const keys = new Set<string>();
  const walk = (nodes: RelationshipTreeNode[]): void => {
    for (const node of nodes) {
      keys.add(node.key);
      if (node.children) walk(node.children);
    }
  };
  walk(trees);
  return keys;
};

type ConfiguredRelationshipTreeProps = {
  value: RelationshipTreeValue | null | undefined;
  onChange: (value: RelationshipTreeValue) => void;
  config: RelationshipTreeConfig;
  disabled?: boolean;
  status?: '' | 'error' | 'warning' | undefined;
  id?: string;
};

const ConfiguredRelationshipTree: FC<ConfiguredRelationshipTreeProps> = ({
  value,
  onChange,
  config,
  disabled,
  status,
  id,
}) => {
  const { messageApi } = useContext(MessageContext);
  const { token } = theme.useToken();
  const { md } = Grid.useBreakpoint();
  const formSnap = useSnapshot(formStore.state);
  const formId = formSnap.formId;
  const versionId = formSnap.versionId;
  const formTypeId = formSnap.form.data?.formType;

  const { workflows: applicableWorkflows } = useApplicableWorkflows({
    triggerType: 'formAction',
    formType: formTypeId,
    enabled: formId !== undefined,
  });

  const trees = useMemo(() => value?.trees ?? [], [value?.trees]);
  const winningRecordIds = useMemo(
    () => value?.winningRecordIds ?? [],
    [value?.winningRecordIds],
  );

  /**
   * The `trees` this component last rendered. `value.trees` is live — it reaches
   * here from `formVersion.data` through `useFieldUI`'s snapshot, and
   * `mergeDocumentIntoProxy` replaces arrays by reference — so a handler
   * suspended at an `await` holds an array another session may already have
   * superseded. Committing that closure would write the pre-run tree back over
   * their node, with no error anywhere, so every post-await commit reads this
   * ref instead (SYD-2569).
   */
  const treesRef = useRef(trees);
  treesRef.current = trees;

  const winningKeySet = useMemo(
    () => computeWinningKeySet(trees, winningRecordIds),
    [trees, winningRecordIds],
  );
  const treeHasWinning = winningRecordIds.length > 0 && winningKeySet.size > 0;

  const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(
    () => new Set(),
  );

  const expandedKeySet = useMemo(() => {
    const all = collectAllKeys(trees);
    const expanded = new Set<string>();
    all.forEach((key) => {
      if (!collapsedKeys.has(key)) expanded.add(key);
    });
    return expanded;
  }, [collapsedKeys, trees]);

  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(
    null,
  );
  const [workflowModal, setWorkflowModal] = useState<Workflow | null>(null);
  const [workflowInitialValues, setWorkflowInitialValues] = useState<
    Record<string, unknown> | undefined
  >(undefined);
  const [autoSelectTargets, setAutoSelectTargets] = useState(false);

  const commit = useCallback(
    (nextTrees: RelationshipTreeNode[], nextWinning?: string[]) => {
      const winning = nextWinning ?? winningRecordIds;
      const reconciled =
        winning.length > 0 &&
        computeWinningKeySet(nextTrees, winning).size === 0
          ? []
          : winning;
      onChange({
        trees: nextTrees,
        winningRecordIds: reconciled,
      });
    },
    [onChange, winningRecordIds],
  );

  const { isRunning, runWorkflow, stopRun, stopPhase } = useWorkflowRunner({
    sourceFormId: formId,
    sourceVersionId: versionId,
  });

  const interactionDisabled = !!disabled || isRunning;

  const runWorkflowFromTree = useCallback(
    async (
      workflow: Workflow,
      params: Record<string, unknown>,
      attachments?: WorkflowRunAttachments,
    ): Promise<WorkflowRunOutcome | null> => {
      if (!workflow.docId || !formId || !versionId) return null;
      try {
        const result = await runWorkflow({
          workflow,
          userParameters: params,
          attachments,
        });
        if (!result) return null;
        if (!result.success) {
          const stopped = workflowRunStoppedOutcome(result, workflow);
          messageApi.warning(stopped.text, 10);
          return stopped;
        }

        const successMessage = `${workflow.description?.shortLabel || 'Workflow'} completed.`;
        messageApi.success(successMessage);
        return {
          status: 'success',
          text: successMessage,
          summary: result.summaries?.find((s) => s.actionType === 'send'),
        };
      } catch (err) {
        const outcome = workflowRunErrorOutcome(
          err,
          workflow.description?.shortLabel,
        );
        messageApi.error(outcome.text);
        return outcome;
      }
    },
    [formId, versionId, messageApi, runWorkflow],
  );

  const handleAddRoot = useCallback(() => {
    const newNode = createNode(config.rootEntityId, config);
    commit([...trees, newNode]);
  }, [commit, config, trees]);

  const handleAddChild = useCallback(
    (nodeKey: string, entityOrGroupId: string) => {
      const newNode = createNode(entityOrGroupId, config);
      commit(insertChild(trees, nodeKey, newNode));
    },
    [commit, config, trees],
  );

  const handleAddBranch = useCallback(
    (nodeKey: string, entityOrGroupIds: string[]) => {
      const branch = entityOrGroupIds.reduceRight<
        RelationshipTreeNode | undefined
      >(
        (child, entityOrGroupId) =>
          createNode(
            entityOrGroupId,
            config,
            undefined,
            child ? [child] : undefined,
          ),
        undefined,
      );
      if (!branch) return;
      commit(insertChild(trees, nodeKey, branch));
    },
    [commit, config, trees],
  );

  const handleAddSibling = useCallback(
    (nodeKey: string, entityOrGroupId: string) => {
      const newNode = createNode(entityOrGroupId, config);
      commit(insertSibling(trees, nodeKey, newNode));
    },
    [commit, config, trees],
  );

  const handleDuplicate = useCallback(
    (nodeKey: string) => {
      const node = findNode(trees, nodeKey);
      if (!node) return;
      commit(insertSibling(trees, nodeKey, duplicateNode(node)));
    },
    [commit, trees],
  );

  const requestDelete = useCallback(
    (nodeKey: string) => {
      const node = findNode(trees, nodeKey);
      if (!node) return;
      const descendantCount = countDescendants(node);
      const message =
        descendantCount > 0
          ? `Delete this node and all its ${descendantCount} ${descendantCount === 1 ? 'child' : 'children'}?`
          : 'Delete this node?';
      setPendingDelete({ nodeKey, message });
    },
    [trees],
  );

  const confirmDelete = useCallback(() => {
    if (pendingDelete) {
      commit(removeNode(trees, pendingDelete.nodeKey));
    }
    setPendingDelete(null);
  }, [commit, pendingDelete, trees]);

  const cancelDelete = useCallback(() => setPendingDelete(null), []);

  const handleRecordChange = useCallback(
    (nodeKey: string, entityRecordId: string | undefined) => {
      const shouldValidateUniqueness = Object.values(config.entityConfig).some(
        (entityConfig) => entityConfig?.validateUniqueCombinations,
      );

      if (shouldValidateUniqueness) {
        const entityHierarchy = buildEntityHierarchy(
          config.rootEntityId,
          config,
        );
        const entityLabels = Object.keys(config.entityConfig).reduce(
          (acc, key) => {
            acc[key] = config.entityConfig[key]?.label || key;
            return acc;
          },
          {} as Record<string, string>,
        );
        const validation = validateCombinationUniqueness(
          trees,
          nodeKey,
          entityRecordId,
          entityHierarchy,
          Math.min(config.maxDepth, MAX_TREE_DEPTH),
          entityLabels,
        );
        if (!validation.isValid && validation.errorMessage) {
          messageApi?.warning({
            content: validation.errorMessage,
            duration: 5,
          });
          return;
        }
      }

      commit(updateNode(trees, nodeKey, { entityRecordId }));
    },
    [commit, config, messageApi, trees],
  );

  const buildWorkflowParams = useCallback(
    (node: RelationshipTreeNode): Record<string, unknown> => ({
      node,
      branch: getNodeBranch(trees, node.key),
    }),
    [trees],
  );

  const handleMarkWinning = useCallback(
    async (node: RelationshipTreeNode) => {
      const winningRecordId = node.entityRecordId;
      const matches = winningRecordId
        ? findMarkWinningWorkflows(applicableWorkflows, node.entityOrGroupId)
        : [];
      for (const { workflow, parameterId } of matches) {
        const outcome = await runWorkflowFromTree(workflow, {
          ...buildWorkflowParams(node),
          [parameterId]: winningRecordId,
        });
        if (outcome !== null && outcome.status !== 'success') return;
      }
      const latestTrees = treesRef.current;
      const nextWinning = computeWinningRecordIds(latestTrees, node.key);
      /**
       * Empty means the acted-on node is no longer in the tree another session
       * has since written. Committing the chain then would mark nothing, and
       * committing `[]` would clear a winner this user never touched.
       */
      if (nextWinning.length === 0) return;
      commit(latestTrees, nextWinning);
    },
    [applicableWorkflows, buildWorkflowParams, commit, runWorkflowFromTree],
  );

  const handleUnmarkWinning = useCallback(
    async (node: RelationshipTreeNode) => {
      const matches = findUnmarkWinningWorkflows(
        applicableWorkflows,
        node.entityOrGroupId,
      );
      for (const workflow of matches) {
        const outcome = await runWorkflowFromTree(
          workflow,
          buildWorkflowParams(node),
        );
        if (outcome !== null && outcome.status !== 'success') return;
      }
      commit(treesRef.current, []);
    },
    [applicableWorkflows, buildWorkflowParams, commit, runWorkflowFromTree],
  );

  const renderRow = useCallback(
    (node: RelationshipTreeNode) => {
      const depth = getNodeDepth(trees, node.key);
      const isLastRootNode = depth === 1 && trees.length === 1;
      const isWinningNode = winningKeySet.has(node.key);
      const nodeHasWinningKids = (node.children ?? []).some((child) =>
        winningKeySet.has(child.key),
      );
      const hasRecordSelected = !!node.entityRecordId;
      const hasCompletePath = hasCompletePathWithRecords(trees, node.key);

      const sendItems: MenuProps['items'] = (() => {
        if (!formId || !versionId) return [];
        const sendMatches = findSendWorkflowsForNode(
          applicableWorkflows,
          node.entityOrGroupId,
        );
        const allRecordIds = collectRecordIds(trees);
        return sendMatches.flatMap(({ workflow, mode }) => {
          if (mode === 'single') {
            if (!node.entityRecordId) return [];
            return [
              {
                key: `workflow-send-one:${workflow.docId ?? ''}:${node.key}`,
                label: workflow.description?.shortLabel || workflow.docId,
                icon: <MailOutlined />,
                disabled: interactionDisabled,
                onClick: () => {
                  setWorkflowInitialValues(buildWorkflowParams(node));
                  setWorkflowModal(workflow);
                },
              },
            ];
          }
          if (mode === 'multi') {
            if (depth !== 1 || allRecordIds.length === 0) return [];
            return [
              {
                key: `workflow-send-all:${workflow.docId ?? ''}:${node.key}`,
                label: `${workflow.description?.shortLabel || workflow.docId} (Send all)`,
                icon: <MailOutlined />,
                disabled: interactionDisabled,
                onClick: () => {
                  setWorkflowInitialValues(buildWorkflowParams(node));
                  setAutoSelectTargets(true);
                  setWorkflowModal(workflow);
                },
              },
            ];
          }
          return [];
        });
      })();

      const buildMenu = ({ openNotes }: { openNotes: () => void }) =>
        buildNodeMenu({
          node,
          depth,
          config,
          treeHasWinningCombination: treeHasWinning,
          isWinning: isWinningNode,
          hasRecordSelected,
          nodeHasWinningChildren: nodeHasWinningKids,
          hasCompletePath,
          isLastRootNode,
          onAddChild: (entityOrGroupId) =>
            handleAddChild(node.key, entityOrGroupId),
          onAddBranch: (entityOrGroupIds) =>
            handleAddBranch(node.key, entityOrGroupIds),
          onAddSibling: (entityOrGroupId) =>
            handleAddSibling(node.key, entityOrGroupId),
          onDuplicate: () => handleDuplicate(node.key),
          onDelete: () => requestDelete(node.key),
          onMarkWinning: () => {
            void handleMarkWinning(node);
          },
          onUnmarkWinning: () => {
            void handleUnmarkWinning(node);
          },
          onEditNotes: openNotes,
          sendItems,
        });

      const shouldDisableRecord = interactionDisabled || treeHasWinning;
      /**
       * Mirrors the Delete menu item's own gate. The confirmation outlives the
       * dropdown that spawned it, so a winning combination arriving while it is
       * open would otherwise re-enable a Delete the menu no longer offers — and
       * its removal reconciles `winningRecordIds` to `[]` behind an entity
       * reference that stays written (SYD-2569).
       */
      const deleteConfirmDisabled = interactionDisabled || treeHasWinning;

      return (
        <NodeRow
          nodeKey={node.key}
          value={node.entityRecordId}
          onChange={(entityRecordId) =>
            handleRecordChange(node.key, entityRecordId)
          }
          disabled={shouldDisableRecord}
          treeType={node.treeType}
          entityOrGroupId={node.entityOrGroupId}
          buildMenu={buildMenu}
          menuDisabled={interactionDisabled}
          isWinning={isWinningNode}
          extraInformation={node.extraInformation}
          onExtraInfoChange={(next) =>
            commit(updateNode(trees, node.key, { extraInformation: next }))
          }
          recipientConfig={
            node.treeType === 'recipient' ? node.recipientConfig : undefined
          }
          onRecipientConfigChange={
            node.treeType === 'recipient'
              ? (recipientConfig) =>
                  commit(updateNode(trees, node.key, { recipientConfig }))
              : undefined
          }
          config={config}
          deleteConfirmOpen={pendingDelete?.nodeKey === node.key}
          deleteConfirmMessage={pendingDelete?.message}
          deleteConfirmDisabled={deleteConfirmDisabled}
          onDeleteConfirm={confirmDelete}
          onDeleteCancel={cancelDelete}
        />
      );
    },
    [
      applicableWorkflows,
      buildWorkflowParams,
      cancelDelete,
      commit,
      config,
      confirmDelete,
      formId,
      handleAddBranch,
      handleAddChild,
      handleAddSibling,
      handleDuplicate,
      handleMarkWinning,
      handleRecordChange,
      handleUnmarkWinning,
      interactionDisabled,
      pendingDelete,
      requestDelete,
      treeHasWinning,
      trees,
      versionId,
      winningKeySet,
    ],
  );

  const toggleExpand = useCallback((key: string) => {
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const addRootLabel = (() => {
    const rootConfig = config.entityConfig[config.rootEntityId];
    const baseLabel = rootConfig?.abbreviation ?? rootConfig?.label ?? 'Root';
    return `${baseLabel} Tree (Sibling)`;
  })();

  const borderColor =
    status === 'error'
      ? token.colorError
      : status === 'warning'
        ? token.colorWarning
        : undefined;

  return (
    <FlexBox
      id={id ?? 'relationship-tree-container'}
      column
      alignStart
      gap={0}
      stretch
      style={{
        width: '100%',
        ...(md
          ? null
          : {
              maxWidth: `var(${FORM_PAGE_CSS_VARS.formContentMaxWidth}, 100%)`,
              overflowX: 'auto',
            }),
        ...(borderColor
          ? {
              border: `1px solid ${borderColor}`,
              borderRadius: token.borderRadius,
              padding: token.paddingXXS,
            }
          : {}),
      }}
    >
      {trees.length === 0 ? (
        <Button
          icon={<PlusOutlined />}
          onClick={handleAddRoot}
          disabled={interactionDisabled}
          aria-label="add-root-node"
          type="text"
          style={{ backgroundColor: token.colorFillTertiary }}
        >
          Add {addRootLabel}
        </Button>
      ) : (
        trees.map((node) => (
          <TreeNode
            key={node.key}
            node={node}
            expandedKeys={expandedKeySet}
            toggleExpand={toggleExpand}
            renderNodeRow={renderRow}
          />
        ))
      )}
      <WorkflowParameterSidebar
        workflow={workflowModal}
        open={workflowModal !== null}
        sourceFormId={formId}
        sourceVersionId={versionId}
        initialValues={workflowInitialValues}
        autoSelectAll={autoSelectTargets}
        onCancel={() => {
          setWorkflowModal(null);
          setWorkflowInitialValues(undefined);
          setAutoSelectTargets(false);
        }}
        onConfirm={async (params, attachments) => {
          const w = workflowModal;
          setWorkflowInitialValues(undefined);
          if (!w) return null;
          return runWorkflowFromTree(w, params, attachments);
        }}
        onStop={stopRun}
        stopPhase={stopPhase}
        formNumber={formSnap.form.data?.formNumber}
        formTitle={formSnap.form.data?.formNumber ?? 'Form'}
      />
    </FlexBox>
  );
};

const RelationshipTree: FC<RelationshipTreeProps> = ({
  value,
  onChange,
  config,
  disabled,
  status,
  id,
}) => {
  if (!config) {
    return (
      <Button disabled aria-label="relationship-tree-missing-config">
        Relationship Tree (no configuration)
      </Button>
    );
  }
  return (
    <ConfiguredRelationshipTree
      value={value}
      onChange={onChange}
      config={config}
      disabled={disabled}
      status={status}
      id={id}
    />
  );
};

export default RelationshipTree;
